import React, { useEffect, useRef, useState } from 'react';
import { Spin, message, Select } from 'antd';
import * as echarts from 'echarts';
import { getPersonalLeadDistribution, PersonalLeadDistributionData } from '../../../services/statistics';

const { Option } = Select;

const PersonalLeadDistributionChart: React.FC = () => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState<PersonalLeadDistributionData | null>(null);
  const [days, setDays] = useState(7);

  // 加载数据
  const loadData = async (selectedDays: number) => {
    setLoading(true);
    try {
      const data = await getPersonalLeadDistribution(selectedDays);

      // 验证数据格式
      if (!data || !data.dates || !data.employees || !data.data) {
        console.error('数据格式错误:', data);
        message.error('数据格式错误');
        setChartData(null);
        return;
      }

      setChartData(data);
    } catch (error) {
      console.error('Failed to load personal lead distribution data:', error);
      message.error('加载数据失败');
      setChartData(null);
    } finally {
      setLoading(false);
    }
  };

  // 初始加载
  useEffect(() => {
    loadData(days);
  }, []);

  // 时间维度变化时重新加载
  const handleDaysChange = (value: number) => {
    setDays(value);
    loadData(value);
  };

  // 渲染图表
  useEffect(() => {
    if (!chartRef.current || !chartData) return;

    // Initialize chart
    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    // 检查是否有员工数据
    if (!chartData.employees || chartData.employees.length === 0) {
      chartInstance.current.clear();
      return;
    }

    // Ant Design 色系
    const colors = [
      '#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2',
      '#eb2f96', '#fa8c16', '#a0d911', '#2f54eb', '#fa541c', '#597ef7'
    ];

    // 构建系列数据（每个员工一条折线）
    const series = chartData.employees.map((employee, index) => ({
      name: employee,
      type: 'line',
      data: chartData.data[employee] || [],
      smooth: true,
      itemStyle: {
        color: colors[index % colors.length],
      },
      lineStyle: {
        width: 2,
      },
      symbol: 'circle',
      symbolSize: 6,
    }));

    const option = {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
        },
      },
      legend: {
        data: chartData.employees,
        bottom: 0,
        type: 'scroll',
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: chartData.employees.length > 6 ? '15%' : '12%',
        top: '3%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: chartData.dates,
        axisLabel: {
          formatter: (value: string) => {
            // 格式化日期显示（只显示月-日）
            const date = new Date(value);
            return `${date.getMonth() + 1}-${date.getDate()}`;
          },
        },
      },
      yAxis: {
        type: 'value',
        name: '跟进线索数',
        minInterval: 1,
      },
      series: series,
    };

    try {
      chartInstance.current.setOption(option);
    } catch (error) {
      console.error('图表渲染失败:', error);
    }

    // Handle resize
    const handleResize = () => {
      chartInstance.current?.resize();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [chartData]);

  return (
    <Spin spinning={loading}>
      <div style={{ marginBottom: 16, textAlign: 'right' }}>
        <Select
          value={days}
          onChange={handleDaysChange}
          style={{ width: 120 }}
        >
          <Option value={7}>近7天</Option>
          <Option value={14}>近14天</Option>
          <Option value={30}>近30天</Option>
        </Select>
      </div>
      <div ref={chartRef} style={{ width: '100%', height: '300px' }} />
    </Spin>
  );
};

export default PersonalLeadDistributionChart;
